import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GameCartridge } from '@/types';
import { elevenLabsMusicService, MusicTrack } from '@/services/elevenlabsMusicService';

interface MusicPlayerProps {
  cartridge?: GameCartridge | null;
}

export const MusicPlayer: React.FC<MusicPlayerProps> = ({ cartridge }) => {
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const activeTrack = tracks[current];

  useEffect(() => {
    const run = async () => {
      if (!cartridge) return;
      setLoading(true);
      setError(null);
      try {
        // Ensure at least one track per cartridge
        const list = await elevenLabsMusicService.ensurePlaylistForCartridge(cartridge, { numTracks: 1, duration: 60, styleHint: `${cartridge.worldSettings?.setting || 'cyberpunk'} ambient` });
        setTracks(list);
      } catch (e: any) {
        setError(e?.message || 'Failed to load music');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [cartridge?.id]);

  useEffect(() => {
    if (audioRef.current && activeTrack?.url) {
      // Autoplay softly
      audioRef.current.volume = 0.4;
      audioRef.current.play().catch(() => {});
    }
  }, [activeTrack?.url]);

  if (!cartridge) return null;

  return (
    <div className="bg-black/50 backdrop-blur-md rounded-lg p-4 border border-purple-500/30">
      <h3 className="text-lg font-bold text-purple-300 mb-2">Soundscape</h3>
      {loading && <div className="text-sm text-gray-400">Generating music...</div>}
      {error && <div className="text-sm text-red-400">{error}</div>}
      {!loading && tracks.length === 0 && (
        <div className="text-sm text-gray-400">No tracks available.</div>
      )}
      {activeTrack && (
        <div>
          <div className="text-cyan-400 text-sm">{activeTrack.title}</div>
          <audio ref={audioRef} src={activeTrack.url} controls className="w-full mt-2" />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setCurrent((i) => Math.max(0, i - 1))}
              className="px-3 py-1 bg-gray-800 rounded border border-gray-600 hover:bg-gray-700">
              Prev
            </button>
            <button
              onClick={() => setCurrent((i) => (i + 1) % tracks.length)}
              className="px-3 py-1 bg-gray-800 rounded border border-gray-600 hover:bg-gray-700">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicPlayer;

